/**
 * The confirmation of "Align B to A" (CU-869fhuhy6, UX v3 D21, criterion
 * 29) — the same dialog from the Rate shops panel and from the foot of the
 * "When does it run?" card. Shows what changes (frequency, window, days,
 * hours) as Now · After and what stays (offices, pickup / dropoff
 * time), plus the saving the engine computed for the pair. Pure helpers +
 * one small table; the caller owns `useConfirm`.
 */
import React from 'react'
import { Box, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material'
import { dsColorNew } from '@palette'
import { fmtInt, hoursLabel, runDaysLabel, runHours } from 'Pages/DsRepricing/Page/@services/cadence'

/** The four lines criterion 29 names, from a cadence. */
export function cadenceFacts(c) {
	const specific = c?.frequencyKind === 'specific_hours'
	return {
		frequency: specific ? 'Specific hours' : `Every ${c?.frequencyValue ?? '—'} h`,
		window: specific ? '—' : `${c?.windowStart || '—'}–${c?.windowEnd || '—'}`,
		days: runDaysLabel(c?.weekdays),
		hours: hoursLabel(runHours(c)) || 'no run hour',
	}
}

const ROWS = [['Frequency', 'frequency'], ['Window', 'window'], ['Days it runs', 'days'], ['Run hours', 'hours']]

export function AlignDetail({ before, after, saves }) {
	const a = cadenceFacts(before), b = cadenceFacts(after)
	const cell = { fontSize: 13, py: 0.5, px: 1, borderBottom: `1px solid ${dsColorNew.grayBorder}` }
	return (
		<Box>
			{/* Inside the confirm's detail box, not a data table: the head is a quiet small-caps line, so
			    the theme's navy head is set aside here (bgcolor and colour both set, as every grey head). */}
			<Table size="small" sx={{ '& td, & th': cell }}>
				<TableHead>
					<TableRow sx={{ '& th': { bgcolor: 'transparent', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.6,
					                          color: dsColorNew.textSubtle, borderRadius: 0 } }}>
						<TableCell/><TableCell>Now</TableCell><TableCell>After</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{ROWS.map(([label, k]) => (
						<TableRow key={k}>
							<TableCell sx={{ color: dsColorNew.textMuted }}>{label}</TableCell>
							<TableCell sx={{ textDecoration: a[k] !== b[k] ? 'line-through' : 'none', color: a[k] !== b[k] ? dsColorNew.textSubtle : dsColorNew.textDark }}>{a[k]}</TableCell>
							<TableCell sx={{ fontWeight: a[k] !== b[k] ? 600 : 400, color: dsColorNew.textTitle }}>{b[k]}</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
			{saves != null && (
				<Box sx={{ mt: 1, fontSize: 13, color: dsColorNew.textDark }}>
					Saves ≈ <strong>{fmtInt(saves)}</strong> searches / day, by the engine's count.
				</Box>
			)}
		</Box>
	)
}

/** The options for `confirm(...)`: title, body, detail and the verb on the button. */
export function alignConfirmOptions({ aName, bName, before, after, saves }) {
	return {
		title: `Align "${bName}" with "${aName}"?`,
		body: `${bName} will run when ${aName} runs; its pickup / dropoff time, offices and everything else stay.`,
		detail: <AlignDetail before={before} after={after} saves={saves}/>,
		confirmLabel: 'Align',
	}
}
